import { Row, Space, Spacing, Txt } from "@stenajs-webui/core";
import {
  Card,
  CardBody,
  CardHeader,
  FlatButton,
  Icon,
  stenaAngleDown,
  stenaAngleRight,
  stenaCheck,
} from "@stenajs-webui/elements";
import { StandardTable } from "@stenajs-webui/grid";
import { orderBy, uniq } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "@/redux/RootReducer";
import { createExpectedVolumeTableConfig } from "../config/ExpectedVolumeTableConfig";
import {
  expectedVolumeActions,
  expectedVolumeSelectors,
} from "../redux/ExpectedVolumeRedux";
import { copyLastYear } from "../redux/thunks/CopyLastYear";

interface Props {
  routePairCode: string;
  statNo: string;
  hideIfNoItems: boolean;
  selectedYear: number;
}

const currentYear = new Date().getUTCFullYear();

export const ExpectedVolumeForRoutePair: React.FC<Props> = ({
  routePairCode,
  statNo,
  hideIfNoItems,
  selectedYear,
}) => {
  const dispatch = useDispatch();

  const isVolumeAgreedSelector = useCallback(
    (state: StoreState) =>
      expectedVolumeSelectors.getIsVolumeAgreedByRoutePair(
        state,
        routePairCode
      ),
    [routePairCode]
  );
  const isVolumeAgreed = useSelector(isVolumeAgreedSelector);
  const yearIsEditable = selectedYear >= currentYear;
  const tableIsEditable = yearIsEditable && !isVolumeAgreed;

  const config = useMemo(
    () =>
      createExpectedVolumeTableConfig(routePairCode, tableIsEditable, dispatch),
    [dispatch, routePairCode, tableIsEditable]
  );

  const selector = useCallback(
    (state: StoreState) =>
      expectedVolumeSelectors.getExpectedVolumeForRoutePairId(
        state,
        routePairCode
      ),
    [routePairCode]
  );

  const routePairEntity = useSelector(selector);

  const [expanded, setExpanded] = useState(false);

  const entityHasVolume = routePairEntity?.routePairMeta.hasVolume;
  const uniqCustomerNumbersLength = uniq(
    routePairEntity?.volumes.map((v) => v.persisted.customerNo)
  ).length;

  useEffect(() => {
    if (entityHasVolume !== undefined && uniqCustomerNumbersLength <= 8) {
      setExpanded(entityHasVolume);
    }
  }, [entityHasVolume, uniqCustomerNumbersLength]);

  const onClickClear = useCallback(() => {
    dispatch(expectedVolumeActions.clearExpectedVolume(routePairCode));
  }, [dispatch, routePairCode]);

  const onClickCopy = useCallback(() => {
    dispatch(copyLastYear(routePairCode));
    setExpanded(true);
  }, [dispatch, routePairCode]);

  if (
    !routePairEntity ||
    (hideIfNoItems && !routePairEntity.routePairMeta.hasVolume)
  ) {
    return null;
  }

  const sortedItems = orderBy(routePairEntity.volumes ?? [], [
    (item) => !item.editable.customerIsParent,
    (item) => item.editable.id !== statNo,
  ]);

  return (
    <Card maxWidth={"1900px"}>
      <CardHeader
        text={routePairCode}
        contentLeft={
          <FlatButton
            leftIcon={expanded ? stenaAngleDown : stenaAngleRight}
            onClick={() => setExpanded((expanded) => !expanded)}
          />
        }
        contentRight={
          <Row alignItems={"center"}>
            {yearIsEditable && !isVolumeAgreed ? (
              <>
                <FlatButton label={"Copy 12m vol"} onClick={onClickCopy} />
                <Space />
                <FlatButton
                  label={"Clear expected volume"}
                  disabled={!routePairEntity.routePairMeta.hasVolume}
                  onClick={onClickClear}
                />
                <FlatButton
                  label={"Set as agreed volume"}
                  onClick={() => {
                    dispatch(
                      expectedVolumeActions.setVolumeAgreedForRoutePair(
                        routePairCode,
                        true
                      )
                    );
                  }}
                />
              </>
            ) : (
              yearIsEditable && (
                <FlatButton
                  label={"Unset as agreed volume"}
                  onClick={() => {
                    dispatch(
                      expectedVolumeActions.setVolumeAgreedForRoutePair(
                        routePairCode,
                        false
                      )
                    );
                  }}
                />
              )
            )}
          </Row>
        }
      />
      {expanded && (
        <CardBody>
          <StandardTable config={config} items={sortedItems} />
          {isVolumeAgreed && (
            <>
              <Row indent={3} gap={1} alignItems={"center"}>
                <Icon color="#404032" icon={stenaCheck} />
                <Txt color="#34603F">Agreed volume</Txt>
              </Row>
              <Spacing num={1} />
            </>
          )}
        </CardBody>
      )}
    </Card>
  );
};
